import { defaultToolId, getToolDefinition, isModeId, isToolId } from "./transforms";
import type { Locale, ToolId, ToolModeId } from "./types";

export const HANDOFF_MESSAGE_VERSION = 1 as const;
export const READY_MESSAGE_TYPE = "justtool:ready";
export const PREFILL_MESSAGE_TYPE = "justtool:prefill";

const PRODUCTION_TOOL_ORIGIN = "https://justonetools.com";
const PRODUCTION_DOCS_ORIGINS = new Set([
  "https://docs.justoneapi.com",
  "https://docs.justserpapi.com"
]);

export interface StartupState {
  locale: Locale;
  toolId: ToolId;
  modeId: ToolModeId;
}

export interface StartupStateOptions {
  search: string;
  storedToolId: string | null;
  storedModeId: string | null;
}

export interface PrefillPayload {
  tool: ToolId;
  mode: ToolModeId;
  lang: Locale;
  input: string;
}

export interface PrefillMessage {
  type: typeof PREFILL_MESSAGE_TYPE;
  version: typeof HANDOFF_MESSAGE_VERSION;
  payload: PrefillPayload;
}

export interface ReadyMessage {
  type: typeof READY_MESSAGE_TYPE;
  version: typeof HANDOFF_MESSAGE_VERSION;
}

export function resolveStartupState(options: StartupStateOptions): StartupState {
  const params = new URLSearchParams(options.search);
  const toolId = resolveToolId(
    normalizeLegacyToolId(params.get("tool")),
    normalizeLegacyToolId(options.storedToolId)
  );
  const modeId = resolveModeId(
    toolId,
    params.get("mode"),
    options.storedModeId
  );

  return {
    locale: parseLocaleParam(params.get("lang")) ?? "zh",
    toolId,
    modeId
  };
}

export function parseLocaleParam(value: string | null | undefined): Locale | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.toLowerCase();

  if (normalized.startsWith("en")) {
    return "en";
  }

  if (normalized.startsWith("zh")) {
    return "zh";
  }

  return null;
}

export function createReadyMessage(): ReadyMessage {
  return {
    type: READY_MESSAGE_TYPE,
    version: HANDOFF_MESSAGE_VERSION
  };
}

export function parsePrefillMessage(data: unknown): PrefillMessage | null {
  if (!isRecord(data)) {
    return null;
  }

  if (
    data.type !== PREFILL_MESSAGE_TYPE ||
    data.version !== HANDOFF_MESSAGE_VERSION ||
    !isRecord(data.payload)
  ) {
    return null;
  }

  const tool = asStringOrNull(data.payload.tool);
  const mode = asStringOrNull(data.payload.mode);
  const lang = parseLocaleParam(asStringOrNull(data.payload.lang));
  const input = asStringOrNull(data.payload.input);
  const normalizedTool = normalizeLegacyToolId(tool);

  if (
    !isToolId(normalizedTool) ||
    !isModeId(mode) ||
    !lang ||
    input === null
  ) {
    return null;
  }

  const validatedTool: ToolId = normalizedTool;
  const validatedMode: ToolModeId = mode;

  if (!getToolDefinition(validatedTool).modeIds.includes(validatedMode)) {
    return null;
  }

  return {
    type: PREFILL_MESSAGE_TYPE,
    version: HANDOFF_MESSAGE_VERSION,
    payload: {
      tool: validatedTool,
      mode: validatedMode,
      lang,
      input
    }
  };
}

export function isAllowedPrefillOrigin(
  senderOrigin: string,
  receiverOrigin: string
): boolean {
  const sender = parseHttpUrl(senderOrigin);
  const receiver = parseHttpUrl(receiverOrigin);

  if (!sender || !receiver) {
    return false;
  }

  if (receiver.origin === PRODUCTION_TOOL_ORIGIN) {
    return PRODUCTION_DOCS_ORIGINS.has(sender.origin);
  }

  if (isLocalOrigin(receiver) && isLocalOrigin(sender)) {
    return true;
  }

  return false;
}

export function getReferrerOrigin(referrer: string): string | null {
  const url = parseHttpUrl(referrer);
  return url?.origin ?? null;
}

function resolveToolId(queryToolId: string | null, storedToolId: string | null): ToolId {
  if (isToolId(queryToolId)) {
    return queryToolId;
  }

  if (isToolId(storedToolId)) {
    return storedToolId;
  }

  return defaultToolId;
}

function normalizeLegacyToolId(value: string | null): string | null {
  if (value === "html-entity") {
    return "html";
  }

  return value;
}

function resolveModeId(
  toolId: ToolId,
  queryModeId: string | null,
  storedModeId: string | null
): ToolModeId {
  const tool = getToolDefinition(toolId);

  if (isModeId(queryModeId) && tool.modeIds.includes(queryModeId)) {
    return queryModeId;
  }

  if (isModeId(storedModeId) && tool.modeIds.includes(storedModeId)) {
    return storedModeId;
  }

  return tool.defaultModeId;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function isLocalOrigin(origin: URL): boolean {
  return origin.hostname === "localhost" || origin.hostname === "127.0.0.1";
}

function parseHttpUrl(value: string): URL | null {
  try {
    const url = new URL(value);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }

    return url;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
